// Sessão do usuário (GET /auth/me) e helpers de permissão.
import { createContext, useContext, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { MeAccount, MeResponse } from '@impulso/shared'
import { api, ApiError } from './api'

interface SessionValue {
  me: MeResponse | null
  loading: boolean
  /** Falha ao carregar a sessão (rede/servidor) sem resposta anterior: não significa que saiu da conta */
  error: boolean
  retry: () => void
  refresh: () => Promise<void>
  accountForCompany: (companyId: string) => MeAccount | undefined
}

const SessionContext = createContext<SessionValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api.get<MeResponse>('/auth/me')
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null
        throw e
      }
    },
    staleTime: 60_000,
  })
  const value: SessionValue = {
    me: q.data ?? null,
    loading: q.isLoading,
    error: q.isError && q.data === undefined,
    retry: () => void q.refetch(),
    refresh: async () => {
      await qc.invalidateQueries({ queryKey: ['me'] })
    },
    accountForCompany: (companyId) => q.data?.accounts.find((a) => a.companies.some((c) => c.id === companyId)),
  }
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession() {
  const v = useContext(SessionContext)
  if (!v) throw new Error('useSession fora do SessionProvider')
  return v
}
